import { useState } from "react";
import { motion } from "framer-motion";
import { FaImage, FaStickyNote, FaCheck, FaChevronLeft } from "react-icons/fa";
import { AiOutlineClose } from "react-icons/ai";
import Dropdown from "../../../../../Components/UI/Inputs/Dropdown";
import { trackingStatuses } from "../../../../../constants/statuses";
import BarcodeScanner from "../../../../../Components/BarcodeScanner/BarcodeScanner";
import { MdQrCodeScanner } from "react-icons/md";
import TrackingHistory from "./TrackingHistory";
import { useTranslation } from "react-i18next";

const PackageDetails = ({
  pkg,
  shipmentData,
  onAddImage,
  updateFormData,
  formData,
  goBack,
}) => {
  const { t } = useTranslation("carrier");
  const [images, setImages] = useState([]);
  const [note, setNote] = useState();
  const [isScanning, setIsScanning] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  const packageFormData = formData.find((p) => p.id === pkg.barcode);

  const currentStatus = packageFormData?.status || "";
  const isPickedUpOrLater =
    trackingStatuses.indexOf(currentStatus) >=
    trackingStatuses.indexOf("Picked Up");

  const handleImageUpload = (pkg, e) => {
    const files = Array.from(e.target.files);
    setImages([...images, ...files]);
    onAddImage(pkg, e);
  };

  const handleScanSuccess = (scannedBarcode) => {
    if (scannedBarcode === pkg.barcode) {
      updateFormData(pkg.barcode, "status", "Picked Up");
      setIsScanning(false);
    }
  };
  const handlePickupByBarcode = (barcode) => {
    setBarcodeInput("");
    updateFormData(barcode, "status", "Picked Up");
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -5 }}
      className="transition"
    >
      {!isScanning ? (
        <>
          <div className="w-full flex items-center justify-between pb-1 mb-1">
            <span className="font-semibold text-sm">{t("dispatcher.packageDetails")}</span>
            <span
              onClick={goBack}
              className="flex items-center justify-center px-1.5 py-[2px] cursor-pointer rounded-sm font-medium bg-ui-surface-sunken text-xs text-ui-text-muted"
            >
              <FaChevronLeft size={10} className="mr-0.5" />
              {t("dispatcher.back")}
            </span>
          </div>
          <div className="grid grid-cols-2 text-xs">
            <div className="my-[2px]">
              <strong>{t("dispatcher.idLabel")}</strong> {pkg.parcel_id}
            </div>
            <div className="my-[2px]">
              <strong>{t("dispatcher.barcodeLabel")}</strong> {pkg.barcode}
            </div>
            <div className="my-[2px]">
              <strong>{t("dispatcher.weightLabel")}</strong> {pkg.weight}{" "}
              {shipmentData.unit === "Imperial" ? "lb" : "kg"}
            </div>
            <div className="my-[2px]">
              <strong>{t("dispatcher.sizeLabel")}</strong> {pkg.length} x {pkg.width} x {pkg.height}{" "}
              {shipmentData.unit === "Imperial" ? "in" : "cm"}
            </div>
          </div>
          <div className="my-[2px] text-xs">
            <strong>{t("dispatcher.descriptionLabel")}</strong> {pkg.description || "N/A"}
          </div>

          <div className="w-full mb-1 mt-2">
            {isPickedUpOrLater ? (
              <Dropdown
                placeholder={t("dispatcher.selectStatus")}
                options={trackingStatuses}
                onChange={(status) =>
                  updateFormData(pkg.barcode, "status", status)
                }
                value={currentStatus}
              />
            ) : (
              <div className="w-full flex flex-col items-center gap-1">
                {/* Scan Button */}
                <button
                  className="w-full h-7 bg-brand text-white text-[10px] rounded-md shadow-glass flex items-center justify-center gap-1 hover:bg-brand-hover"
                  onClick={() => setIsScanning(true)}
                >
                  <MdQrCodeScanner size={12} />
                  {t("dispatcher.scanToPickUp")}
                </button>
                <div className="text-lightgray text-[10px] font-medium text-center w-full ">
                  {t("dispatcher.orCaps")}
                </div>
                {/* Manual Barcode Input */}
                <input
                  type="text"
                  placeholder={t("dispatcher.enterBarcode")}
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  className="w-full h-7 border border-ui-border rounded-lg px-3 text-xs focus:outline-none"
                />

                {/* Add Barcode Button (Appears if barcode exists) */}
                {barcodeInput === pkg?.barcode && (
                  <motion.button
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="w-full h-7 bg-state-success-solid text-white text-[10px] font-medium rounded-lg shadow-md flex items-center justify-center gap-1 hover:bg-state-success-solid-hover"
                    onClick={() => handlePickupByBarcode(barcodeInput)}
                  >
                    {t("dispatcher.pickUp")}
                  </motion.button>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-1 mt-2">
            {isPickedUpOrLater ? (
              <button className="flex items-center justify-center w-full h-7 bg-state-success-solid text-white text-[10px] rounded-md shadow-glass">
                <FaCheck size={12} className="mr-1" /> {t("dispatcher.scanned")}
              </button>
            ) : (
              <button
                className="flex items-center justify-center w-full h-7 bg-brand-hover text-white text-[10px] rounded-md shadow-glass hover:bg-brand-hover"
                onClick={() => setIsScanning(true)}
              >
                <MdQrCodeScanner size={12} className="mr-1" /> {t("dispatcher.scan")}
              </button>
            )}

            <button className="relative flex items-center justify-center w-full h-7 bg-ui-border text-ui-text text-[10px] rounded-md shadow-glass cursor-pointer hover:bg-soft-gray transition">
              <FaImage size={12} className="mr-1" />
              {t("dispatcher.image")}
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleImageUpload(pkg, e)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </button>

            <button
              className="flex items-center justify-center w-full h-7 bg-state-warning-solid text-white text-[10px] rounded-md shadow-glass hover:bg-state-warning-solid-hover"
              onClick={() => setNote((prev) => !prev)}
            >
              <FaStickyNote size={12} className="mr-1" /> {t("dispatcher.note")}
            </button>
          </div>

          {(note ||
            (packageFormData?.description &&
              packageFormData?.description !== "")) && (
            <motion.textarea
              initial={{ opacity: 0, y: -3 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full mt-1 p-1 border border-ui-border rounded-md text-[10px]"
              placeholder={t("dispatcher.writeANote")}
              onChange={(e) =>
                updateFormData(pkg.barcode, "description", e.target.value)
              }
              value={packageFormData?.description}
            />
          )}

          {packageFormData?.images?.length > 0 && (
            <div className="mt-1 flex flex-wrap space-x-1">
              {packageFormData?.images?.map((img, index) => (
                <img
                  key={index}
                  src={URL.createObjectURL(img)}
                  alt={t("dispatcher.uploadedAlt", { n: index })}
                  className="w-10 h-10 rounded-md object-cover border border-ui-border"
                />
              ))}
            </div>
          )}
          {(packageFormData?.description ||
            packageFormData?.images?.length > 0 ||
            packageFormData?.status !== pkg.tracking_status) && (
            <button
              onClick={goBack}
              className="w-full mt-2 h-7 bg-brand text-white text-[10px] rounded-md shadow-glass"
            >
              {t("dispatcher.done")}
            </button>
          )}
          <TrackingHistory data={pkg?.shipment_histories} />
        </>
      ) : (
        <div className="relative w-full h-[205px] top-0 left-0 z-[100]">
          <div className="w-full h-full">
            <button
              className="absolute z-[120] top-2 right-2 flex items-center gap-2 bg-ui-inverted text-white p-1.5 rounded-md shadow-md hover:bg-ui-inverted transition"
              onClick={() => setIsScanning(false)}
            >
              <AiOutlineClose size={16} />
            </button>

            <BarcodeScanner
              setData={handleScanSuccess}
              handleClose={() => setIsScanning(false)}
            />
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default PackageDetails;
